import { FaroTextButton } from "@components/common/faro-text-button";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useMarkupContext } from "@context-providers/markup/markup-context";
import { BaseProjectIdProps } from "@custom-types/base-types";
import { Markup } from "@custom-types/project-markups-types";
import { blue, useToast } from "@flat-ui";
import { IElementAttachment } from "@faro-lotv/ielement-types";
import { useProjectMarkupUpdate } from "@hooks/project-markups/use-project-markup-update";
import { DeleteMarkupDialog } from "@pages/project-details/project-markups/markup-delete-dialog";
import { nounPluralize } from "@utils/data-display";
import {
  BulkActionEvents,
  TableType,
} from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { cloneDeep, findIndex } from "es-toolkit/compat";
import { useState } from "react";

interface Props extends BaseProjectIdProps {
  /** The selected attachments to delete */
  selectedAttachments: IElementAttachment[];

  /** The selected markup */
  selectedMarkup: Markup;

  /** Function to set the selected attachments */
  setSelectedAttachments: (selectedAttachments: Set<string>) => void;
}

/** Show delete attachments button and dispatch action where the delete dialog will act accordingly */
export function BulkDeleteMarkupAttachments({
  selectedAttachments,
  projectId,
  selectedMarkup,
  setSelectedAttachments,
}: Props): JSX.Element {
  const [shouldShowDialog, setShouldShowDialog] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const { bulkDeleteAttachmentsFromMarkup } = useProjectMarkupUpdate({
    projectId,
  });
  const { openToast } = useToast();
  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorContext();
  const { markups, updateMarkups, attachments, updateAttachments } =
    useMarkupContext();

  /** Delete markup's attachments in bulk */
  async function deleteMarkupAttachmentsInBulk(): Promise<void> {
    trackEvent({
      name: BulkActionEvents.bulkDeleteMarkupAttachment,
      props: {
        dataType: TableType.markupAttachments,
        numberOfEntities: selectedAttachments.length,
      },
    });

    try {
      setIsDeleting(true);

      const response = await bulkDeleteAttachmentsFromMarkup(
        selectedAttachments
      );
      if (response && response.failedMutations.length === 0) {
        openToast({
          title: "Attachments deleted.",
        });
      } else {
        openToast({
          title: `Failed to delete ${nounPluralize({
            counter: response.failedMutations.length,
            word: "Attachment",
          })}`,
          variant: "error",
        });
      }

      // Update the attachments of the annotation
      const updatedAttachments = attachments.filter(
        (attachment) =>
          !response.successMutations.some(
            (deletion) => deletion.elementId === attachment.id
          )
      );
      updateAttachments(updatedAttachments);

      // Also update the value of the attachmentsCount property of the annotation, which is used in the annotations table
      const newMarkups = cloneDeep(markups);
      const markupIndex = findIndex(newMarkups, { id: selectedMarkup.id });
      if (markupIndex !== -1) {
        newMarkups[markupIndex].attachmentsCount = updatedAttachments.length;
        updateMarkups(newMarkups);
      }
    } catch (error) {
      handleErrorWithToast({
        id: `Delete attachments-${Date.now().toString()}`,
        title: "Failed to delete the annotation's attachments.",
        error,
      });
    } finally {
      setIsDeleting(false);
      setShouldShowDialog(false);
      setSelectedAttachments(new Set());
    }
  }
  return (
    <>
      <FaroTextButton
        onClick={() => setShouldShowDialog(true)}
        tooltipText="Delete attachments"
        sx={{
          fontWeight: 600,
          paddingX: "10px",
          paddingY: "6px",
          width: "60px",
          color: blue[500],
        }}
      >
        Delete
      </FaroTextButton>

      {shouldShowDialog && (
        <DeleteMarkupDialog
          dialogContent={{
            title: "Delete attachments?",
            description:
              "This will remove the selected attachments from your project.",
          }}
          onClose={() => setShouldShowDialog(false)}
          onConfirm={deleteMarkupAttachmentsInBulk}
          isDeleting={isDeleting}
          shouldShowWarning={false}
        />
      )}
    </>
  );
}
